import { getJWTToken } from '../../utils/auth';
import { authHeaders } from '../../core/impersonation';

const apiUrl = import.meta.env.REACT_APP_BAMBOO_API_URL ?? '';

export type ProfileStanding = 'active' | 'monitoring' | 'former' | 'departed' | 'unknown' | 'waived';
export type ProfileCohort = 'ltel' | 'newcomer' | 'dually_identified';
export type ProfileRole = 'admin' | 'teacher';

export type ProfileStudent = {
  id: string;
  firstName?: string;
  lastName?: string;
  grade?: string;
  school?: string;
  elTeachers: string[];
  programModel?: string;
  homeLanguage?: string;
};

export type ProfileElStatus = {
  activeSinceYearEnd?: number;
  yearsInProgram?: number;
  exitYearEnd?: number;
  exitMonth?: number;
  exitReason?: string;
};

export type ProfileAdministration = {
  schoolYearEnd: number;
  month?: number;
  grade?: string;
  scale: 'access' | 'alternate_access';
  composite: number | null;
  domains?: Record<string, number>;
};

export type ProfileAccess = {
  scale: 'access' | 'alternate_access';
  inFile: boolean;
  noScores: boolean;
  administrations: ProfileAdministration[];
};

export type ProfilePlan = { studentId: string; schoolYear: string; generatedAt: string };

export type StudentProfile = {
  student: ProfileStudent;
  standing: ProfileStanding;
  lastSeenSchoolYearEnd?: number;
  cohorts: ProfileCohort[];
  elStatus: ProfileElStatus;
  access: ProfileAccess;
  plans: ProfilePlan[];
  plansKnown: boolean;
  plansGenerated: boolean;
  plansGeneratedKnown: boolean;
  monitoringYears: number;
  monitoringYearsKnown: boolean;
  currentSchoolYear: string;
  role: ProfileRole;
};

export class StudentProfileFetchError extends Error {
  status: number;

  constructor(status: number) {
    super(`profile fetch failed: ${status}`);
    this.name = 'StudentProfileFetchError';
    this.status = status;
  }
}

export async function fetchStudentProfile(studentId: string): Promise<StudentProfile> {
  const token = await getJWTToken();
  const response = await fetch(`${apiUrl}/students/${encodeURIComponent(studentId)}/profile`, {
    headers: authHeaders(token),
  });
  if (!response.ok) throw new StudentProfileFetchError(response.status);
  return (await response.json()) as StudentProfile;
}
